import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Heading, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import useMotionAllowed from './useMotionAllowed';
import { EASE } from './primitives';

/**
 * A headline whose lines never wrap.
 *
 * Give it the lines you want and it keeps exactly those line breaks at every
 * width, shrinking the whole headline until the widest line fits its column.
 * The point is editorial control: a headline broken where the author chose
 * reads deliberately, while one broken by whatever the viewport happened to
 * be reads like an accident, and "Where Surgeons / Do Their Best Work" landing
 * as "Where Surgeons Do Their / Best Work" is a different sentence rhythm.
 *
 * This is layout, not decoration, so it runs regardless of the motion
 * preference. It also runs without JavaScript having animated anything: the
 * lines are real elements and remain readable if the measurement never fires.
 *
 * Measurement happens before paint, again once webfonts land (metrics change
 * when the real face swaps in), and on resize.
 *
 * `reveal` raises each line out of a mask on first render, one after the
 * other, the way a well-set printed headline arrives. It is skipped entirely
 * when the visitor has asked for less motion, leaving the lines in place.
 */
const FitHeadline = ({
  lines = [],
  as = 'h1',
  maxShrink = 0.55,
  reveal = false,
  revealDelay = 0.15,
  ...props
}) => {
  const ref = useRef(null);
  const motionAllowed = useMotionAllowed();
  const animateLines = reveal && motionAllowed;

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // Clear any previous adjustment so the natural size is measured, not the
    // already-shrunk one, which would otherwise ratchet smaller on each pass.
    el.style.fontSize = '';

    const available = el.clientWidth;
    if (!available) return;

    const widest = Array.from(el.querySelectorAll('[data-fit-line]')).reduce(
      (max, line) => Math.max(max, line.scrollWidth),
      0
    );
    if (!widest || widest <= available) return;

    const natural = parseFloat(window.getComputedStyle(el).fontSize);
    // The 0.98 leaves a hair of room so sub-pixel rounding cannot re-wrap it.
    const ratio = Math.max((available / widest) * 0.98, maxShrink);
    el.style.fontSize = `${(natural * ratio).toFixed(2)}px`;
  }, [maxShrink]);

  useLayoutEffect(() => {
    fit();
  });

  useEffect(() => {
    let frame = 0;
    const soon = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };

    window.addEventListener('resize', soon);
    // Webfont metrics differ from the fallback, so re-measure once they load.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', soon);
    };
  }, [fit]);

  return (
    <Heading ref={ref} as={as} {...props}>
      {lines.map((line, i) => (
        <Box
          key={i}
          as="span"
          data-fit-line=""
          display="block"
          whiteSpace="nowrap"
          // The mask for the entrance. A little bottom padding keeps
          // descenders from being clipped by the hidden overflow.
          overflow={animateLines ? 'hidden' : undefined}
          pb={animateLines ? '0.08em' : undefined}
        >
          {animateLines ? (
            <motion.span
              style={{ display: 'inline-block' }}
              initial={{ y: '105%' }}
              animate={{ y: '0%' }}
              transition={{ duration: 1.1, ease: EASE, delay: revealDelay + i * 0.12 }}
            >
              {line}
            </motion.span>
          ) : (
            line
          )}
        </Box>
      ))}
    </Heading>
  );
};

export default FitHeadline;
